// ─────────────────────────────────────────────────────────────────────────────
// Prominent floating card that overlays the Pricing page while the CALM100
// early-access program has spots remaining. Steals the eye BEFORE the plan
// picker so late-arrivers don't miss the offer.
//
// Behaviour:
//   • Spots remaining (>0): shows a big "Early Access — First 100 Only" card
//     with the promo code input pre-expanded + a live remaining count.
//   • Spots gone (0): auto-swaps to a "5-Day Free Trial" callout so the
//     eye-catching slot stays filled with the primary conversion message.
//   • On successful auth downstream (App.js post-auth effect calls
//     redeemPendingPromoCode), the sessionStorage value collected here is
//     redeemed automatically.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check, Clock, ShieldCheck, X } from "lucide-react";
import { usePromoStatus } from "../lib/usePromoStatus";
import { storePendingPromoCode, readPendingPromoCode } from "../lib/promoRedeem";

const CODE = "CALM100";
const DISMISS_KEY = "bc_promo_floating_dismissed";

export const PromoFloatingCard = () => {
  const navigate = useNavigate();
  const { status } = usePromoStatus(CODE);
  const [value, setValue] = useState("");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Hydrate the input from sessionStorage so the value survives an
    // OAuth round-trip if the user came back from Apple/Google.
    const stored = readPendingPromoCode();
    if (stored) setValue(stored);
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  if (dismissed || !status?.exists) return null;
  const { remaining, is_full } = status;
  const isRecognised = value.trim().toUpperCase() === CODE;

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase();
    setValue(v);
    storePendingPromoCode(v);
  };

  const handleClaim = () => {
    if (isRecognised) {
      // Route straight to signup — App.js post-auth effect handles the
      // redemption once the user finishes creating their account.
      navigate("/signup?intent=trial");
    }
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  // ── SPOTS REMAINING → early-access card ─────────────────────────────
  if (!is_full) {
    return (
      <div
        className="fixed z-40 right-4 sm:right-6 lg:right-10 top-24 sm:top-28 w-[calc(100vw-2rem)] sm:w-[380px] max-w-md"
        data-testid="promo-floating-card"
      >
        <div className="relative rounded-2xl bg-gradient-to-br from-[#FFF7ED] via-[#FEF3C7] to-[#FEE2E2] border-2 border-[#F59E0B]/60 shadow-2xl p-5 sm:p-6">
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 p-1.5 rounded-full text-[#9A3412] hover:bg-white/60 transition"
            data-testid="promo-floating-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F6C071] to-[#E29B4C] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="text-[10px] tracking-widest uppercase text-[#9A3412] font-bold">
              Early Access · First 100 Only
            </div>
          </div>

          <h3
            className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] leading-tight mb-2"
            data-testid="promo-floating-title"
          >
            Lifetime free access — for the first 100.
          </h3>
          <p className="text-sm text-[#7C2D12] mb-4 leading-relaxed">
            Enter code <span className="font-mono font-bold tracking-wider">{CODE}</span> below to skip payment forever.{" "}
            <span
              className="font-semibold text-[#9A3412]"
              data-testid="promo-floating-remaining"
            >
              Only {remaining} {remaining === 1 ? "spot" : "spots"} left.
            </span>
          </p>

          <div className="relative mb-3">
            <input
              type="text"
              value={value}
              onChange={handleChange}
              placeholder="Enter CALM100"
              autoComplete="off"
              autoCapitalize="characters"
              className="w-full pl-4 pr-10 py-3 rounded-full text-base font-mono tracking-widest bg-white border-2 border-[#F59E0B] focus:outline-none focus:border-[#D97706] placeholder:font-sans placeholder:tracking-normal placeholder:text-[#9A3412]/50"
              data-testid="promo-floating-input"
            />
            {isRecognised && (
              <Check
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#059669]"
                data-testid="promo-floating-recognised"
              />
            )}
          </div>

          {isRecognised && (
            <div
              className="text-xs text-[#065F46] font-medium mb-3"
              data-testid="promo-floating-recognised-message"
            >
              🎉 Code recognised — sign up below to claim your spot.
            </div>
          )}

          <button
            onClick={handleClaim}
            disabled={!isRecognised}
            className="w-full rounded-full h-11 text-sm font-semibold bg-[#1B2349] text-white hover:bg-[#0F1838] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="promo-floating-claim-btn"
          >
            {isRecognised ? "Claim your free spot →" : "Enter code above to continue"}
          </button>

          <div className="mt-4 flex items-center gap-2 text-[10px] text-[#7C2D12]">
            <ShieldCheck className="w-3 h-3" /> No card required. Lifetime, no expiry.
          </div>
        </div>
      </div>
    );
  }

  // ── SPOTS GONE → keep the eye-catching slot with the trial CTA ──────
  return (
    <div
      className="fixed z-40 right-4 sm:right-6 lg:right-10 top-24 sm:top-28 w-[calc(100vw-2rem)] sm:w-[380px] max-w-md"
      data-testid="promo-floating-card-trial"
    >
      <div className="relative rounded-2xl bg-gradient-to-br from-[#EAF3F6] via-[#F0EAF6] to-[#FBF6EE] border-2 border-[#3090A8]/40 shadow-2xl p-5 sm:p-6">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-1.5 rounded-full text-[#3090A8] hover:bg-white/60 transition"
          data-testid="promo-floating-trial-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3090A8] to-[#1B2349] flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="text-[10px] tracking-widest uppercase text-[#1B2349] font-bold">
            5-Day Free Trial
          </div>
        </div>

        <h3
          className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] leading-tight mb-2"
          data-testid="promo-floating-trial-title"
        >
          Try Bilateral Calm free for 5 days.
        </h3>
        <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">
          Full library. Full practices. No card required to start — we'll send a gentle reminder before day 6.
        </p>

        <button
          onClick={() => navigate("/signup?intent=trial")}
          className="w-full rounded-full h-11 text-sm font-semibold bg-[#1B2349] text-white hover:bg-[#0F1838] active:scale-[0.98] transition"
          data-testid="promo-floating-trial-cta"
        >
          Start your free trial →
        </button>

        <div className="mt-4 flex items-center gap-2 text-[10px] text-[#4A5568]">
          <ShieldCheck className="w-3 h-3" /> Cancel anytime · No card required
        </div>
      </div>
    </div>
  );
};

export default PromoFloatingCard;
